import { ReactElement } from 'react';
import { MdLockOutline } from 'react-icons/md';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Badge } from '@Components/Badge';
import { Design, Placement } from '@Components/designConstants';

/**
 * Marks a chart or dashboard this user may read but not change.
 *
 * Lives under `src/Pages/` rather than `src/Components/`: the wording is specific to dashboarding
 * governance, and nothing outside these two page trees has a use for it.
 *
 * The tooltip names BOTH ways forward on purpose. A colleague who cannot edit a record can take a
 * copy, or ask the owner to widen the edit level — and the second route is cheap, because a creator
 * can grant edit rights to a less privileged tier themselves, with no administrator involved. A
 * badge that mentions only the copy quietly pushes people toward two diverging charts when they
 * wanted one shared chart.
 */
export const ViewOnlyBadge = (): ReactElement => (
  <OverlayTrigger
    placement={Placement.BOTTOM}
    overlay={
      <Tooltip id='view-only-badge-tooltip'>
        You have view-only access. Save a copy to edit, or ask the owner for access.
      </Tooltip>
    }
  >
    {/*
      The <span> is NOT optional. OverlayTrigger clones its child with a ref and with mouse and
      focus handlers; `Badge` forwards no ref and spreads no unknown props, so it drops every one of
      them and the tooltip never appears — with no error anywhere. Do not add ref forwarding to
      `Badge` to avoid this wrapper: that is a change to a shared component, with its own Storybook
      and verification obligations, to save one element.
    */}
    <span className='view-only-badge'>
      <Badge leadingElement={<MdLockOutline />} design={Design.NEUTRAL}>
        View only
      </Badge>
    </span>
  </OverlayTrigger>
);
